'use client';

import { useEffect, useMemo, useState } from 'react';

import { Badge } from '@core/ui/primitives/badge';
import { Button } from '@core/ui/primitives/button';
import { Card, CardContent, CardHeader, CardTitle } from '@core/ui/primitives/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@core/ui/primitives/dialog';
import { Icon } from '@core/ui/primitives/icon';
import { Input } from '@core/ui/primitives/input';
import { Separator } from '@core/ui/primitives/separator';
import { Spinner } from '@core/ui/primitives/spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@core/ui/primitives/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@core/ui/primitives/tabs';
import { Textarea } from '@core/ui/primitives/textarea';
import { cn } from '@core/helpers/misc/GeneralUtils';
import {
  createSubscriptionTier,
  ensureUsageCyclesNow,
  getOrganizationTranscriptionBreakdown,
  grantTranscriptionMinutes,
  resetTranscriptionUsage,
  updateOrganizationSubscription,
  updateSubscriptionTier,
} from '../actions';
import { FeaturePicker, type FeatureOption } from './FeaturePicker';

/**
 * Available subscription features.
 * These should match the features registered in FeatureRegistryService.
 */
const AVAILABLE_FEATURES: FeatureOption[] = [
  {
    id: 'search:basic',
    name: 'Basic Search',
    description: 'BM25 full-text search across vault content',
    category: 'search',
    defaultEnabled: true,
  },
  {
    id: 'search:semantic',
    name: 'Semantic Search',
    description: 'AI-powered semantic search for natural language queries',
    category: 'search',
    defaultEnabled: false,
  },
  {
    id: 'comments',
    name: 'Comments',
    description: 'Inline comments and threaded discussions on documents',
    category: 'collaboration',
    defaultEnabled: false,
  },
  {
    id: 'collaboration:realtime',
    name: 'Real-time Collaboration',
    description: 'Live multi-user editing with cursors',
    category: 'collaboration',
    defaultEnabled: false,
  },
  {
    id: 'transcription',
    name: 'Audio Transcription',
    description: 'Convert audio files to text using AI transcription',
    category: 'content',
    defaultEnabled: false,
  },
  {
    id: 'mindmap',
    name: 'Mind Maps',
    description: 'Create and edit visual mind maps',
    category: 'content',
    defaultEnabled: false,
  },
  {
    id: 'excalidraw',
    name: 'Excalidraw',
    description: 'Collaborative whiteboard and diagramming',
    category: 'content',
    defaultEnabled: true, // Enabled by default like search:basic
  },
];

interface TierDto {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  features: string[];
  /** Feature/entitlement IDs for enforcement */
  privileges: string[];
  maxOwnedOrganizations: number;
  maxStorageMB: number;
  maxTranscriptionMinutesPerMonth: number;
  maxTranscriptionUploadMB: number;
  maxCollabYjsStateMB: number;
  storageWarningPercent: number;
  transcriptionWarningPercent: number;
  isDefault: boolean;
  /** Whether this tier is visible in public plan selection */
  isPublic: boolean;
  sortOrder: number;
  updatedAt: string;
}

interface TierLimitsDto {
  tierId: string;
  slug: string;
  name: string;
  maxStorageMB: number;
  maxTranscriptionMinutesPerMonth: number;
  maxTranscriptionUploadMB: number;
  maxCollabYjsStateMB: number;
  storageWarningPercent: number;
  transcriptionWarningPercent: number;
}

interface OrganizationOverridesDto {
  maxStorageMB?: number;
  maxTranscriptionMinutesPerMonth?: number;
}

interface OrganizationDto {
  id: string;
  name: string;
  slug: string;
  urlSlug: string;
  ownerEmail: string | null;
  tierId: string | null;
  tierSlug: string | null;
  tierName: string | null;
  billingCycleAnchor: string;
  overrides: OrganizationOverridesDto;
  effectiveLimits: TierLimitsDto | null;
  storageUsedBytes: string;
  storageLimitBytes: string | null;
  transcriptionUsedMinutes: number;
  transcriptionLimitMinutes: number;
  cycleStart: string;
  cycleEnd: string;
}

interface TranscriptionBreakdownDto {
  cycleStart: string;
  cycleEnd: string;
  usedMinutes: number;
  limitMinutes: number;
  resetDate: string;
  users: Array<{ userId: string; email: string | null; minutesUsed: number }>;
}

interface SubscriptionAdminViewProps {
  tiers: TierDto[];
  organizations: OrganizationDto[];
  /** Called after successful mutations to reload data */
  onRefresh?: () => void;
}

function formatIsoDate(value: string): string {
  if (!value) return '';
  return value.slice(0, 10);
}

function formatBytes(bytesRaw: string | null): string {
  if (!bytesRaw) return '—';
  const bytes = Number(bytesRaw);
  if (!Number.isFinite(bytes)) return bytesRaw;

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = Math.max(0, bytes);
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  const decimals = unitIndex === 0 ? 0 : value >= 100 ? 0 : value >= 10 ? 1 : 2;
  return `${value.toFixed(decimals)} ${units[unitIndex]}`;
}

function formatQuota(used: number, limit: number): string {
  if (limit <= 0) return `${used} / ∞`;
  return `${used} / ${limit}`;
}

function normalizeNumber(value: number | undefined | null): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '';
  return String(value);
}

export function SubscriptionAdminView({ tiers, organizations, onRefresh }: SubscriptionAdminViewProps) {
  const [createTierOpen, setCreateTierOpen] = useState(false);
  const [createPrivileges, setCreatePrivileges] = useState<string[]>(['search:basic']);
  const [activeTier, setActiveTier] = useState<TierDto | null>(null);
  const [editPrivileges, setEditPrivileges] = useState<string[]>([]);
  const [activeOrg, setActiveOrg] = useState<OrganizationDto | null>(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [breakdownError, setBreakdownError] = useState<string | null>(null);
  const [breakdown, setBreakdown] = useState<TranscriptionBreakdownDto | null>(null);

  useEffect(() => {
    if (!activeOrg) {
      setBreakdown(null);
      setBreakdownError(null);
      setBreakdownLoading(false);
    }
  }, [activeOrg]);

  // Sync edit privileges when active tier changes
  useEffect(() => {
    if (activeTier) {
      setEditPrivileges(activeTier.privileges ?? []);
    }
  }, [activeTier]);

  const sortedTiers = useMemo(() => {
    return [...tiers].sort((a, b) => (a.sortOrder - b.sortOrder) || a.slug.localeCompare(b.slug));
  }, [tiers]);

  const sortedOrganizations = useMemo(() => {
    return [...organizations].sort((a, b) => a.name.localeCompare(b.name));
  }, [organizations]);

  const totalStorageUsed = useMemo(() => {
    let total = 0;
    organizations.forEach((org) => {
      const parsed = Number(org.storageUsedBytes);
      if (Number.isFinite(parsed)) {
        total += parsed;
      }
    });
    return String(total);
  }, [organizations]);

  const loadBreakdown = async (organizationId: string) => {
    setBreakdownLoading(true);
    setBreakdownError(null);
    try {
      const data = await getOrganizationTranscriptionBreakdown(organizationId);
      setBreakdown(data);
    } catch (error) {
      setBreakdownError(error instanceof Error ? error.message : 'Failed to load transcription breakdown.');
    } finally {
      setBreakdownLoading(false);
    }
  };

  return (
    <div className='space-y-8'>
      <div className='rounded-2xl border bg-gradient-to-br from-primary/5 via-background to-background p-6 shadow-sm'>
        <div className='flex flex-col gap-3 md:flex-row md:items-center md:justify-between'>
          <div className='space-y-2'>
            <div className='inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary'>
              <Icon name='LuBadgeCheck' className='h-4 w-4' />
              Subscription console
            </div>
            <h1 className='text-3xl font-bold tracking-tight'>Subscription tiers & quota guardrails</h1>
            <p className='max-w-2xl text-sm text-muted-foreground'>
              Manage DB-driven tier limits, per-organization overrides, and monthly usage resets.
            </p>
          </div>

          <div className='flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end'>
            <form action={ensureUsageCyclesNow}>
              <Button variant='outline' size='lg' className='gap-2'>
                <Icon name='LuRefreshCw' className='h-4 w-4' />
                Ensure cycles
              </Button>
            </form>

            <Dialog open={createTierOpen} onOpenChange={setCreateTierOpen}>
              <DialogTrigger asChild>
                <Button size='lg' className='gap-2'>
                  <Icon name='LuPlus' className='h-4 w-4' />
                  New tier
                </Button>
              </DialogTrigger>
              <DialogContent className='max-w-2xl max-h-[85vh] flex flex-col'>
                <DialogHeader>
                  <DialogTitle>Create subscription tier</DialogTitle>
                  <DialogDescription>Defines the default quotas applied to organizations.</DialogDescription>
                </DialogHeader>

                <form
                  id='create-tier-form'
                  action={async (formData) => {
                    await createSubscriptionTier(formData);
                    setCreateTierOpen(false);
                    onRefresh?.();
                  }}
                  className='flex-1 overflow-y-auto py-4 pr-2 space-y-6'>
                  <div className='grid gap-4 md:grid-cols-2'>
                    <div className='space-y-2'>
                      <label className='text-sm font-medium' htmlFor='tier-slug'>
                        Slug
                      </label>
                      <Input id='tier-slug' name='slug' placeholder='e.g. free, pro, enterprise' required className='bg-muted/40' />
                    </div>
                    <div className='space-y-2'>
                      <label className='text-sm font-medium' htmlFor='tier-name'>
                        Name
                      </label>
                      <Input id='tier-name' name='name' placeholder='e.g. Free' required className='bg-muted/40' />
                    </div>
                  </div>

                  <div className='space-y-2'>
                    <label className='text-sm font-medium' htmlFor='tier-description'>
                      Description
                    </label>
                    <Textarea id='tier-description' name='description' placeholder='Optional notes for admins' className='bg-muted/40' />
                  </div>

                  <div className='space-y-2'>
                    <label className='text-sm font-medium' htmlFor='tier-features'>
                      Marketing Highlights (one per line)
                    </label>
                    <Textarea id='tier-features' name='features' placeholder='5 GB storage&#10;30 min/month transcription&#10;Basic search' rows={3} className='bg-muted/40' />
                    <p className='text-xs text-muted-foreground'>Display text for the pricing page. Not used for enforcement.</p>
                  </div>

                  <div className='space-y-2'>
                    <label className='text-sm font-medium'>Enabled Features</label>
                    <div className='rounded-lg border bg-muted/20 p-4'>
                      <FeaturePicker
                        value={createPrivileges}
                        onChange={setCreatePrivileges}
                        features={AVAILABLE_FEATURES}
                        name='privileges'
                      />
                    </div>
                    <p className='text-xs text-muted-foreground'>These features will be available to organizations on this tier.</p>
                  </div>

                  <div className='grid gap-4 md:grid-cols-3'>
                    <div className='space-y-2'>
                      <label className='text-sm font-medium' htmlFor='tier-max-owned-orgs'>
                        Max owned orgs
                      </label>
                      <Input
                        id='tier-max-owned-orgs'
                        type='number'
                        min={1}
                        name='maxOwnedOrganizations'
                        defaultValue={2}
                        required
                        className='bg-muted/40'
                      />
                      <p className='text-xs text-muted-foreground'>User-level cap (owned organizations).</p>
                    </div>
                    <div className='space-y-2'>
                      <label className='text-sm font-medium' htmlFor='tier-max-storage'>
                        Storage cap (MB)
                      </label>
                      <Input
                        id='tier-max-storage'
                        type='number'
                        min={0}
                        name='maxStorageMB'
                        defaultValue={5120}
                        required
                        className='bg-muted/40'
                      />
                    </div>
                    <div className='space-y-2'>
                      <label className='text-sm font-medium' htmlFor='tier-max-transcription'>
                        Transcription minutes / month
                      </label>
                      <Input
                        id='tier-max-transcription'
                        type='number'
                        min={0}
                        name='maxTranscriptionMinutesPerMonth'
                        defaultValue={30}
                        required
                        className='bg-muted/40'
                      />
                    </div>
                  </div>

                  <div className='grid gap-4 md:grid-cols-2'>
                    <div className='space-y-2'>
                      <label className='text-sm font-medium' htmlFor='tier-max-transcription-upload'>
                        Transcription upload cap (MB)
                      </label>
                      <Input
                        id='tier-max-transcription-upload'
                        type='number'
                        min={1}
                        name='maxTranscriptionUploadMB'
                        defaultValue={30}
                        required
                        className='bg-muted/40'
                      />
                      <p className='text-xs text-muted-foreground'>Max audio size per upload.</p>
                    </div>
                    <div className='space-y-2'>
                      <label className='text-sm font-medium' htmlFor='tier-max-collab-state'>
                        Collab document cap (MB)
                      </label>
                      <Input
                        id='tier-max-collab-state'
                        type='number'
                        min={1}
                        name='maxCollabYjsStateMB'
                        defaultValue={5}
                        required
                        className='bg-muted/40'
                      />
                      <p className='text-xs text-muted-foreground'>Max persisted Yjs state per draft.</p>
                    </div>
                  </div>

                  <div className='grid gap-4 md:grid-cols-2'>
                    <div className='space-y-2'>
                      <label className='text-sm font-medium' htmlFor='tier-storage-warning'>
                        Storage warning (%)
                      </label>
                      <Input
                        id='tier-storage-warning'
                        type='number'
                        min={0}
                        max={100}
                        name='storageWarningPercent'
                        defaultValue={80}
                        required
                        className='bg-muted/40'
                      />
                    </div>
                    <div className='space-y-2'>
                      <label className='text-sm font-medium' htmlFor='tier-transcription-warning'>
                        Transcription warning (%)
                      </label>
                      <Input
                        id='tier-transcription-warning'
                        type='number'
                        min={0}
                        max={100}
                        name='transcriptionWarningPercent'
                        defaultValue={80}
                        required
                        className='bg-muted/40'
                      />
                    </div>
                  </div>

                  <div className='grid gap-4 md:grid-cols-2'>
                    <div className='space-y-2'>
                      <label className='text-sm font-medium' htmlFor='tier-sort-order'>
                        Sort order
                      </label>
                      <Input id='tier-sort-order' type='number' min={0} name='sortOrder' defaultValue={0} className='bg-muted/40' />
                    </div>
                    <div className='space-y-2'>
                      <label className='text-sm font-medium' htmlFor='tier-is-default'>
                        Default tier
                      </label>
                      <div className='flex items-center gap-2'>
                        <input
                          id='tier-is-default'
                          type='checkbox'
                          defaultChecked={false}
                          onChange={(event) => {
                            const form = event.currentTarget.form;
                            if (!form) return;
                            const hidden = form.querySelector<HTMLInputElement>('input[name=\"isDefault\"]');
                            if (!hidden) return;
                            hidden.value = event.currentTarget.checked ? '1' : '0';
                          }}
                          className='h-4 w-4 accent-primary'
                        />
                        <input type='hidden' name='isDefault' defaultValue='0' />
                        <span className='text-sm text-muted-foreground'>Mark as default</span>
                      </div>
                    </div>
                  </div>

                  <div className='space-y-2'>
                    <label className='text-sm font-medium' htmlFor='tier-is-public'>
                      Visibility
                    </label>
                    <div className='flex items-center gap-2'>
                      <input
                        id='tier-is-public'
                        type='checkbox'
                        defaultChecked={true}
                        onChange={(event) => {
                          const form = event.currentTarget.form;
                          if (!form) return;
                          const hidden = form.querySelector<HTMLInputElement>('input[name=\"isPublic\"]');
                          if (!hidden) return;
                          hidden.value = event.currentTarget.checked ? '1' : '0';
                        }}
                        className='h-4 w-4 accent-primary'
                      />
                      <input type='hidden' name='isPublic' defaultValue='1' />
                      <span className='text-sm text-muted-foreground'>Public tier (visible in plan selection)</span>
                    </div>
                    <p className='text-xs text-muted-foreground'>
                      Private tiers are hidden from users and can only be assigned by admins (e.g., demo, enterprise).
                    </p>
                  </div>
                </form>

                <DialogFooter className='pt-4 border-t'>
                  <Button variant='outline' onClick={() => setCreateTierOpen(false)}>
                    Cancel
                  </Button>
                  <Button type='submit' form='create-tier-form'>
                    Create tier
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className='mt-6 grid gap-4 md:grid-cols-3'>
          <Card className='border-primary/20 bg-primary/5'>
            <CardHeader className='flex flex-row items-center justify-between pb-2'>
              <CardTitle className='text-sm font-medium'>Tiers</CardTitle>
              <span className='rounded-full bg-primary/10 p-2 text-primary'>
                <Icon name='LuLayers' className='h-4 w-4' />
              </span>
            </CardHeader>
            <CardContent className='pt-0'>
              <div className='text-3xl font-bold'>{tiers.length}</div>
              <p className='text-xs text-muted-foreground'>DB-defined configurations</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className='flex flex-row items-center justify-between pb-2'>
              <CardTitle className='text-sm font-medium'>Organizations</CardTitle>
              <span className='rounded-full bg-primary/10 p-2 text-primary'>
                <Icon name='LuBuilding2' className='h-4 w-4' />
              </span>
            </CardHeader>
            <CardContent className='pt-0'>
              <div className='text-3xl font-bold'>{organizations.length}</div>
              <p className='text-xs text-muted-foreground'>Quota enforcement scope</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className='flex flex-row items-center justify-between pb-2'>
              <CardTitle className='text-sm font-medium'>Storage used</CardTitle>
              <span className='rounded-full bg-primary/10 p-2 text-primary'>
                <Icon name='LuDatabase' className='h-4 w-4' />
              </span>
            </CardHeader>
            <CardContent className='pt-0'>
              <div className='text-3xl font-bold'>{formatBytes(totalStorageUsed)}</div>
              <p className='text-xs text-muted-foreground'>Tracked by file deltas</p>
            </CardContent>
          </Card>
        </div>
      </div>

      <Tabs defaultValue='tiers' className='space-y-4'>
        <TabsList>
          <TabsTrigger value='tiers' className='gap-2'>
            <Icon name='LuGauge' className='h-4 w-4' />
            Tiers
          </TabsTrigger>
          <TabsTrigger value='organizations' className='gap-2'>
            <Icon name='LuUsers' className='h-4 w-4' />
            Organizations
          </TabsTrigger>
        </TabsList>

        <TabsContent value='tiers'>
          <Card>
            <CardHeader className='flex flex-col gap-2 md:flex-row md:items-center md:justify-between'>
              <div className='space-y-1'>
                <CardTitle className='text-base'>Tier limits</CardTitle>
                <p className='text-sm text-muted-foreground'>Edit quotas without redeploying.</p>
              </div>
              <Badge variant='outline' className='text-[10px]'>
                {tiers.length} tiers
              </Badge>
            </CardHeader>
            <CardContent className='overflow-x-auto'>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tier</TableHead>
                    <TableHead>Owned orgs</TableHead>
                    <TableHead>Storage</TableHead>
                    <TableHead>Transcription</TableHead>
                    <TableHead>Visibility</TableHead>
                    <TableHead>Default</TableHead>
                    <TableHead className='text-right'>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedTiers.map((tier) => (
                    <TableRow key={tier.id}>
                      <TableCell>
                        <div className='flex items-center gap-2'>
                          <Badge variant={tier.isDefault ? 'default' : 'outline'} className='gap-1'>
                            <Icon name='LuTag' className='h-3.5 w-3.5' />
                            {tier.slug}
                          </Badge>
                          <div className='min-w-0'>
                            <div className='font-medium leading-tight'>{tier.name}</div>
                            {tier.description ? (
                              <div className='text-xs text-muted-foreground line-clamp-2'>{tier.description}</div>
                            ) : null}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className='text-sm'>
                        <div className='font-medium'>{tier.maxOwnedOrganizations.toLocaleString()}</div>
                      </TableCell>
                      <TableCell className='text-sm'>
                        <div className='font-medium'>{tier.maxStorageMB.toLocaleString()} MB</div>
                        <div className='text-xs text-muted-foreground'>Warn @ {tier.storageWarningPercent}%</div>
                      </TableCell>
                      <TableCell className='text-sm'>
                        <div className='font-medium'>{tier.maxTranscriptionMinutesPerMonth.toLocaleString()} min/mo</div>
                        <div className='text-xs text-muted-foreground'>Warn @ {tier.transcriptionWarningPercent}%</div>
                      </TableCell>
                      <TableCell>
                        {tier.isPublic ? (
                          <Badge variant='outline' className='gap-1'>
                            <Icon name='LuGlobe' className='h-3.5 w-3.5' />
                            Public
                          </Badge>
                        ) : (
                          <Badge variant='secondary' className='gap-1'>
                            <Icon name='LuLock' className='h-3.5 w-3.5' />
                            Private
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {tier.isDefault ? (
                          <Badge variant='default' className='gap-1'>
                            <Icon name='LuStar' className='h-3.5 w-3.5' />
                            Default
                          </Badge>
                        ) : (
                          <span className='text-xs text-muted-foreground'>—</span>
                        )}
                      </TableCell>
                      <TableCell className='text-right'>
                        <Button variant='outline' size='sm' onClick={() => setActiveTier(tier)}>
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value='organizations'>
          <Card>
            <CardHeader className='flex flex-col gap-2 md:flex-row md:items-center md:justify-between'>
              <div className='space-y-1'>
                <CardTitle className='text-base'>Organization subscriptions</CardTitle>
                <p className='text-sm text-muted-foreground'>Per-org tier selection, overrides, and usage.</p>
              </div>
              <Badge variant='outline' className='text-[10px]'>
                {organizations.length} orgs
              </Badge>
            </CardHeader>
            <CardContent className='overflow-x-auto'>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Organization</TableHead>
                    <TableHead>Tier</TableHead>
                    <TableHead>Storage</TableHead>
                    <TableHead>Transcription</TableHead>
                    <TableHead>Resets</TableHead>
                    <TableHead className='text-right'>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedOrganizations.map((org) => (
                    <TableRow key={org.id}>
                      <TableCell>
                        <div className='space-y-1'>
                          <div className='font-medium leading-tight'>{org.name}</div>
                          <div className='text-xs text-muted-foreground'>
                            {org.slug}
                            {org.ownerEmail ? ` • ${org.ownerEmail}` : ''}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant='outline' className='gap-1'>
                          <Icon name='LuBadge' className='h-3.5 w-3.5' />
                          {org.tierSlug ?? 'default'}
                        </Badge>
                      </TableCell>
                      <TableCell className='text-sm'>
                        {formatBytes(org.storageUsedBytes)}
                        {org.storageLimitBytes ? (
                          <span className='text-muted-foreground'> / {formatBytes(org.storageLimitBytes)}</span>
                        ) : (
                          <span className='text-muted-foreground'> / ∞</span>
                        )}
                      </TableCell>
                      <TableCell className='text-sm'>
                        {formatQuota(org.transcriptionUsedMinutes, org.transcriptionLimitMinutes)}
                      </TableCell>
                      <TableCell className='text-sm'>{formatIsoDate(org.cycleEnd)}</TableCell>
                      <TableCell className='text-right'>
                        <Button variant='outline' size='sm' onClick={() => setActiveOrg(org)}>
                          Manage
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={Boolean(activeTier)} onOpenChange={(open) => (!open ? setActiveTier(null) : null)}>
        <DialogContent className='max-w-2xl max-h-[85vh] flex flex-col'>
          <DialogHeader>
            <DialogTitle>Edit tier</DialogTitle>
            <DialogDescription>Changes apply immediately to new quota checks.</DialogDescription>
          </DialogHeader>

          {activeTier ? (
            <form
              id='edit-tier-form'
              action={async (formData) => {
                await updateSubscriptionTier(formData);
                setActiveTier(null);
                onRefresh?.();
              }}
              className='flex-1 overflow-y-auto py-4 pr-2 space-y-6'>
              <input type='hidden' name='tierId' value={activeTier.id} />

              <div className='grid gap-4 md:grid-cols-2'>
                <div className='space-y-2'>
                  <label className='text-sm font-medium'>Slug</label>
                  <Input value={activeTier.slug} disabled className='bg-muted/40' />
                </div>
                <div className='space-y-2'>
                  <label className='text-sm font-medium' htmlFor='edit-tier-name'>
                    Name
                  </label>
                  <Input id='edit-tier-name' name='name' defaultValue={activeTier.name} required className='bg-muted/40' />
                </div>
              </div>

              <div className='space-y-2'>
                <label className='text-sm font-medium' htmlFor='edit-tier-description'>
                  Description
                </label>
                <Textarea
                  id='edit-tier-description'
                  name='description'
                  defaultValue={activeTier.description ?? ''}
                  className='bg-muted/40'
                />
              </div>

              <div className='space-y-2'>
                <label className='text-sm font-medium' htmlFor='edit-tier-features'>
                  Marketing Highlights (one per line)
                </label>
                <Textarea
                  id='edit-tier-features'
                  name='features'
                  defaultValue={activeTier.features.join('\n')}
                  rows={3}
                  className='bg-muted/40'
                />
                <p className='text-xs text-muted-foreground'>Display text for the pricing page. Not used for enforcement.</p>
              </div>

              <div className='space-y-2'>
                <label className='text-sm font-medium'>Enabled Features</label>
                <div className='rounded-lg border bg-muted/20 p-4'>
                  <FeaturePicker
                    value={editPrivileges}
                    onChange={setEditPrivileges}
                    features={AVAILABLE_FEATURES}
                    name='privileges'
                  />
                </div>
                <p className='text-xs text-muted-foreground'>These features will be available to organizations on this tier.</p>
              </div>

              <div className='grid gap-4 md:grid-cols-3'>
                <div className='space-y-2'>
                  <label className='text-sm font-medium' htmlFor='edit-tier-max-owned-orgs'>
                    Max owned orgs
                  </label>
                  <Input
                    id='edit-tier-max-owned-orgs'
                    type='number'
                    min={1}
                    name='maxOwnedOrganizations'
                    defaultValue={activeTier.maxOwnedOrganizations}
                    required
                    className='bg-muted/40'
                  />
                  <p className='text-xs text-muted-foreground'>User-level cap (owned organizations).</p>
                </div>
                <div className='space-y-2'>
                  <label className='text-sm font-medium' htmlFor='edit-tier-storage'>
                    Storage cap (MB)
                  </label>
                  <Input
                    id='edit-tier-storage'
                    type='number'
                    min={0}
                    name='maxStorageMB'
                    defaultValue={activeTier.maxStorageMB}
                    required
                    className='bg-muted/40'
                  />
                </div>
                <div className='space-y-2'>
                  <label className='text-sm font-medium' htmlFor='edit-tier-transcription'>
                    Transcription minutes / month
                  </label>
                  <Input
                    id='edit-tier-transcription'
                    type='number'
                    min={0}
                    name='maxTranscriptionMinutesPerMonth'
                    defaultValue={activeTier.maxTranscriptionMinutesPerMonth}
                    required
                    className='bg-muted/40'
                  />
                </div>
              </div>

              <div className='grid gap-4 md:grid-cols-2'>
                <div className='space-y-2'>
                  <label className='text-sm font-medium' htmlFor='edit-tier-transcription-upload'>
                    Transcription upload cap (MB)
                  </label>
                  <Input
                    id='edit-tier-transcription-upload'
                    type='number'
                    min={1}
                    name='maxTranscriptionUploadMB'
                    defaultValue={activeTier.maxTranscriptionUploadMB}
                    required
                    className='bg-muted/40'
                  />
                </div>
                <div className='space-y-2'>
                  <label className='text-sm font-medium' htmlFor='edit-tier-collab-state'>
                    Collab document cap (MB)
                  </label>
                  <Input
                    id='edit-tier-collab-state'
                    type='number'
                    min={1}
                    name='maxCollabYjsStateMB'
                    defaultValue={activeTier.maxCollabYjsStateMB}
                    required
                    className='bg-muted/40'
                  />
                </div>
              </div>

              <div className='grid gap-4 md:grid-cols-2'>
                <div className='space-y-2'>
                  <label className='text-sm font-medium' htmlFor='edit-tier-storage-warning'>
                    Storage warning (%)
                  </label>
                  <Input
                    id='edit-tier-storage-warning'
                    type='number'
                    min={0}
                    max={100}
                    name='storageWarningPercent'
                    defaultValue={activeTier.storageWarningPercent}
                    required
                    className='bg-muted/40'
                  />
                </div>
                <div className='space-y-2'>
                  <label className='text-sm font-medium' htmlFor='edit-tier-transcription-warning'>
                    Transcription warning (%)
                  </label>
                  <Input
                    id='edit-tier-transcription-warning'
                    type='number'
                    min={0}
                    max={100}
                    name='transcriptionWarningPercent'
                    defaultValue={activeTier.transcriptionWarningPercent}
                    required
                    className='bg-muted/40'
                  />
                </div>
              </div>

              <div className='grid gap-4 md:grid-cols-2'>
                <div className='space-y-2'>
                  <label className='text-sm font-medium' htmlFor='edit-tier-sort'>
                    Sort order
                  </label>
                  <Input id='edit-tier-sort' type='number' min={0} name='sortOrder' defaultValue={activeTier.sortOrder} className='bg-muted/40' />
                </div>
                <div className='space-y-2'>
                  <label className='text-sm font-medium'>Default tier</label>
                  <div className='flex items-center gap-2'>
                    <input
                      type='checkbox'
                      defaultChecked={activeTier.isDefault}
                      onChange={(event) => {
                        const form = event.currentTarget.form;
                        if (!form) return;
                        const hidden = form.querySelector<HTMLInputElement>('input[name=\"isDefault\"]');
                        if (!hidden) return;
                        hidden.value = event.currentTarget.checked ? '1' : '0';
                      }}
                      className='h-4 w-4 accent-primary'
                    />
                    <input type='hidden' name='isDefault' defaultValue={activeTier.isDefault ? '1' : '0'} />
                    <span className='text-sm text-muted-foreground'>Set as default</span>
                  </div>
                </div>
              </div>

              <div className='space-y-2'>
                <label className='text-sm font-medium'>Visibility</label>
                <div className='flex items-center gap-2'>
                  <input
                    type='checkbox'
                    defaultChecked={activeTier.isPublic}
                    onChange={(event) => {
                      const form = event.currentTarget.form;
                      if (!form) return;
                      const hidden = form.querySelector<HTMLInputElement>('input[name=\"isPublic\"]');
                      if (!hidden) return;
                      hidden.value = event.currentTarget.checked ? '1' : '0';
                    }}
                    className='h-4 w-4 accent-primary'
                  />
                  <input type='hidden' name='isPublic' defaultValue={activeTier.isPublic ? '1' : '0'} />
                  <span className='text-sm text-muted-foreground'>Public tier (visible in plan selection)</span>
                </div>
                <p className='text-xs text-muted-foreground'>
                  Private tiers are hidden from users and can only be assigned by admins (e.g., demo, enterprise).
                </p>
              </div>
            </form>
          ) : null}

          <DialogFooter className='pt-4 border-t'>
            <Button variant='outline' onClick={() => setActiveTier(null)}>
              Cancel
            </Button>
            <Button type='submit' form='edit-tier-form' disabled={!activeTier}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(activeOrg)} onOpenChange={(open) => (!open ? setActiveOrg(null) : null)}>
        <DialogContent className='max-w-3xl max-h-[85vh] flex flex-col'>
          <DialogHeader>
            <DialogTitle>Manage organization</DialogTitle>
            <DialogDescription>Adjust the tier, overrides, and monthly usage counters for this organization.</DialogDescription>
          </DialogHeader>

          {activeOrg ? (
            <div className='flex-1 overflow-y-auto py-4 pr-2 space-y-6'>
              <div className='rounded-xl border bg-muted/30 p-4 space-y-3'>
                <div className='flex flex-col gap-2 md:flex-row md:items-center md:justify-between'>
                  <div className='space-y-1'>
                    <div className='font-semibold'>{activeOrg.name}</div>
                    <div className='text-xs text-muted-foreground'>
                      {activeOrg.slug} • {activeOrg.id}
                    </div>
                  </div>
                  <Badge variant='outline' className='gap-1 w-fit'>
                    <Icon name='LuBadge' className='h-3.5 w-3.5' />
                    {activeOrg.tierSlug ?? 'default'} tier
                  </Badge>
                </div>

                <Separator />

                <div className='grid gap-3 md:grid-cols-3'>
                  <div>
                    <div className='text-xs uppercase tracking-wider text-muted-foreground'>Storage</div>
                    <div className='text-sm font-medium'>
                      {formatBytes(activeOrg.storageUsedBytes)}{' '}
                      <span className='text-muted-foreground'>
                        / {activeOrg.storageLimitBytes ? formatBytes(activeOrg.storageLimitBytes) : '∞'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <div className='text-xs uppercase tracking-wider text-muted-foreground'>Transcription</div>
                    <div className='text-sm font-medium'>
                      {formatQuota(activeOrg.transcriptionUsedMinutes, activeOrg.transcriptionLimitMinutes)}
                    </div>
                  </div>
                  <div>
                    <div className='text-xs uppercase tracking-wider text-muted-foreground'>Resets</div>
                    <div className='text-sm font-medium'>{formatIsoDate(activeOrg.cycleEnd)}</div>
                  </div>
                </div>
              </div>

              <form
                id='org-subscription-form'
                action={async (formData) => {
                  await updateOrganizationSubscription(formData);
                  setActiveOrg(null);
                  onRefresh?.();
                }}
                className='space-y-4 rounded-xl border p-4'>
                <input type='hidden' name='organizationId' value={activeOrg.id} />

                <div className='grid gap-4 md:grid-cols-2'>
                  <div className='space-y-2'>
                    <label className='text-sm font-medium' htmlFor='org-tier'>
                      Tier
                    </label>
                    <select
                      id='org-tier'
                      name='tierId'
                      defaultValue={activeOrg.tierId ?? ''}
                      className={cn(
                        'h-9 w-full rounded-md border bg-muted/20 px-3 text-sm shadow-xs outline-none transition-[color,box-shadow]',
                        'focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/30',
                      )}>
                      <option value=''>Default tier</option>
                      {sortedTiers.map((tier) => (
                        <option key={tier.id} value={tier.id}>
                          {tier.name} ({tier.slug})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className='space-y-2'>
                    <label className='text-sm font-medium' htmlFor='org-anchor'>
                      Billing cycle anchor (UTC date)
                    </label>
                    <Input
                      id='org-anchor'
                      type='date'
                      name='billingCycleAnchor'
                      defaultValue={formatIsoDate(activeOrg.billingCycleAnchor)}
                      className='bg-muted/20'
                    />
                  </div>
                </div>

                <div className='space-y-2'>
                  <div className='flex items-center justify-between'>
                    <label className='text-sm font-medium'>Quota overrides</label>
                    <Badge variant='outline' className='text-[10px]'>
                      blank = tier default
                    </Badge>
                  </div>
                  <div className='grid gap-4 md:grid-cols-2'>
                    <div className='space-y-1'>
                      <label className='text-xs text-muted-foreground' htmlFor='override-storage'>
                        Storage cap (MB)
                      </label>
                      <Input
                        id='override-storage'
                        type='number'
                        min={0}
                        name='overrideMaxStorageMB'
                        defaultValue={normalizeNumber(activeOrg.overrides.maxStorageMB)}
                        placeholder={activeOrg.effectiveLimits ? String(activeOrg.effectiveLimits.maxStorageMB) : '—'}
                        className='bg-muted/20'
                      />
                    </div>
                    <div className='space-y-1'>
                      <label className='text-xs text-muted-foreground' htmlFor='override-transcription'>
                        Transcription minutes / month
                      </label>
                      <Input
                        id='override-transcription'
                        type='number'
                        min={0}
                        name='overrideMaxTranscriptionMinutesPerMonth'
                        defaultValue={normalizeNumber(activeOrg.overrides.maxTranscriptionMinutesPerMonth)}
                        placeholder={
                          activeOrg.effectiveLimits ? String(activeOrg.effectiveLimits.maxTranscriptionMinutesPerMonth) : '—'
                        }
                        className='bg-muted/20'
                      />
                    </div>
                  </div>
                  <p className='text-xs text-muted-foreground'>
                    Overrides are stored on the organization record. Enforcement always uses the organization’s tier + overrides.
                  </p>
                </div>
              </form>

              <div className='rounded-xl border p-4 space-y-4'>
                <div className='flex items-center justify-between'>
                  <div className='space-y-1'>
                    <div className='font-medium'>Transcription adjustments</div>
                    <div className='text-sm text-muted-foreground'>Grant minutes or reset the current cycle usage.</div>
                  </div>
                </div>

                <div className='grid gap-4 md:grid-cols-2'>
                  <form
                    action={async (formData) => {
                      await grantTranscriptionMinutes(formData);
                      await loadBreakdown(activeOrg.id);
                      onRefresh?.();
                    }}
                    className='rounded-lg border bg-muted/20 p-4 space-y-3'>
                    <input type='hidden' name='organizationId' value={activeOrg.id} />
                    <div className='text-sm font-medium'>Grant minutes</div>
                    <div className='flex items-center gap-2'>
                      <Input type='number' min={1} name='minutes' placeholder='30' className='bg-background' required />
                      <Button type='submit' className='shrink-0'>
                        Apply
                      </Button>
                    </div>
                    <div className='text-xs text-muted-foreground'>
                      Subtracts from used minutes in the current billing cycle and logs an audit event.
                    </div>
                  </form>

                  <form
                    action={async (formData) => {
                      await resetTranscriptionUsage(formData);
                      await loadBreakdown(activeOrg.id);
                      onRefresh?.();
                    }}
                    className='rounded-lg border bg-muted/20 p-4 space-y-3'>
                    <input type='hidden' name='organizationId' value={activeOrg.id} />
                    <div className='text-sm font-medium'>Reset cycle usage</div>
                    <Button type='submit' variant='destructive' className='w-full'>
                      Reset to 0
                    </Button>
                    <div className='text-xs text-muted-foreground'>Sets used minutes to 0 for the current billing cycle and logs an audit event.</div>
                  </form>
                </div>
              </div>

              <div className='rounded-xl border p-4 space-y-4'>
                <div className='flex flex-col gap-2 md:flex-row md:items-center md:justify-between'>
                  <div className='space-y-1'>
                    <div className='font-medium'>Per-user transcription breakdown</div>
                    <div className='text-sm text-muted-foreground'>UsageEvent attribution (current billing cycle).</div>
                  </div>
                  <Button
                    variant='outline'
                    onClick={() => loadBreakdown(activeOrg.id)}
                    disabled={breakdownLoading}
                    className='gap-2'>
                    {breakdownLoading ? (
                      <>
                        <Spinner className='h-4 w-4' />
                        Loading
                      </>
                    ) : (
                      <>
                        <Icon name='LuRefreshCw' className='h-4 w-4' />
                        Load
                      </>
                    )}
                  </Button>
                </div>

                {breakdownError ? (
                  <div className='rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive'>
                    {breakdownError}
                  </div>
                ) : null}

                {breakdown ? (
                  <div className='space-y-3'>
                    <div className='flex flex-wrap items-center gap-2 text-sm'>
                      <Badge variant='outline'>{formatQuota(breakdown.usedMinutes, breakdown.limitMinutes)} min</Badge>
                      <span className='text-muted-foreground'>Resets on {formatIsoDate(breakdown.resetDate)}</span>
                    </div>
                    <div className='overflow-x-auto'>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>User</TableHead>
                            <TableHead className='text-right'>Minutes</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {breakdown.users.length ? (
                            breakdown.users.map((entry) => (
                              <TableRow key={entry.userId}>
                                <TableCell className='text-sm'>{entry.email ?? entry.userId}</TableCell>
                                <TableCell className='text-right text-sm font-medium'>{entry.minutesUsed}</TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={2} className='text-sm text-muted-foreground'>
                                No transcription usage recorded in this cycle.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                ) : (
                  <div className='text-sm text-muted-foreground'>Click Load to query usage events for this cycle.</div>
                )}
              </div>
            </div>
          ) : null}

          <DialogFooter className='pt-4 border-t'>
            <Button variant='outline' onClick={() => setActiveOrg(null)}>
              Close
            </Button>
            <Button type='submit' form='org-subscription-form' disabled={!activeOrg}>
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
